import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { db } from "../../../../firebase";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
} from "firebase/firestore";
import {
  fetchHomePageChallenges,
  fetchSubmissions,
} from "../../../../services/challengeService";
import CustomButton from "../atoms/CustomButton";

const ChallengeList = ({ theme, event }) => {
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);

  const toggleSelect = (id) => {
    setSelectedSubmissions((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    // If all submissions are already selected, deselect all; otherwise, select all
    if (submissions.every((sub) => selectedSubmissions.includes(sub.id))) {
      setSelectedSubmissions([]);
    } else {
      setSelectedSubmissions(submissions.map((sub) => sub.id));
    }
  };

  const handleSave = async () => {
    const selectedObjects = submissions.filter((sub) =>
      selectedSubmissions.includes(sub.id)
    );
    // console.log("Saved submissions:", selectedObjects);

    try {
      // Reference the 'content' subcollection under the event document
      const contentCollectionRef = collection(
        db,
        "events",
        event.id,
        "content"
      );

      // Retrieve all current content documents in the subcollection
      const currentContentSnapshot = await getDocs(contentCollectionRef);

      // Filter documents belonging to the selected challenge only
      const currentContentDocs = currentContentSnapshot.docs.filter((doc) => {
        const data = doc.data();
        return data.challengeId === selectedChallenge.id;
      });
      const currentContentIds = currentContentDocs.map((doc) => doc.id);

      // Determine which submissions to add/update for the current challenge
      const toAddOrUpdate = selectedObjects;
      // Determine which submissions to remove for the current challenge
      const toRemove = currentContentIds.filter(
        (id) => !selectedSubmissions.includes(id)
      );

      // Add or update selected submissions in the subcollection
      await Promise.all(
        toAddOrUpdate.map(async (submission) => {
          const contentDocRef = doc(contentCollectionRef, submission.id);
          await setDoc(contentDocRef, submission, { merge: true });
        })
      );

      // Remove deselected submissions from the subcollection (current challenge only)
      await Promise.all(
        toRemove.map(async (id) => {
          const contentDocRef = doc(contentCollectionRef, id);
          await deleteDoc(contentDocRef);
        })
      );

      handleClose();

      //   console.log("Content subcollection updated successfully.");
    } catch (error) {
      console.error("Error updating content subcollection:", error);
    }
  };

  useEffect(() => {
    const loadChallengesWithSubmissions = async () => {
      try {
        const allChallenges = await fetchHomePageChallenges();

        // For each challenge, check if there's at least one submission
        const challengesWithSubmissions = await Promise.all(
          allChallenges.map(async (challenge) => {
            const { submissions } = await fetchSubmissions(challenge.id, {
              limitNumber: 1,
            });
            return submissions && submissions.length > 0 ? challenge : null;
          })
        );

        // Filter out null values where no submissions were found
        const filteredChallenges = challengesWithSubmissions.filter(Boolean);
        setChallenges(filteredChallenges);
        // console.log("Challenges with submissions:", filteredChallenges);
      } catch (error) {
        console.error("Error fetching challenges or submissions:", error);
      }
    };

    loadChallengesWithSubmissions();
  }, []);

  const handleSelectChallenge = async (challenge) => {
    setSelectedChallenge(challenge);
    setOpen(true);
    try {
      // Fetch submissions for the selected challenge
      const { submissions } = await fetchSubmissions(challenge.id);
      //   console.log("submissions: ", submissions);
      setSubmissions(submissions);

      // Reference the 'content' subcollection under the event document
      const contentCollectionRef = collection(
        db,
        "events",
        event.id,
        "content"
      );
      // Fetch existing content documents
      const contentSnapshot = await getDocs(contentCollectionRef);
      const savedIds = contentSnapshot.docs.map((doc) => doc.id);

      // Preselect submissions that are in the saved content
      const preselected = submissions
        .map((sub) => sub.id)
        .filter((id) => savedIds.includes(id));
      setSelectedSubmissions(preselected);
    } catch (error) {
      console.error("Error fetching submissions or content:", error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedChallenge(null);
    setSubmissions([]);
  };

  return (
    <>
      <Box sx={{ marginTop: 4 }}>
        {/* Header */}
        <Typography
          variant="h5"
          sx={{
            fontFamily: "basic-sans",
            fontWeight: 700,
            color: "white",
          }}
        >
          Select Event Content by Challenge
        </Typography>
      </Box>
      <Grid container spacing={4} sx={{ marginTop: "1px" }}>
        {challenges.map((challenge) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            key={challenge.id}
            sx={{ display: "flex" }}
          >
            <Card
              sx={{
                backgroundColor: theme.colors.mediumPurple,
                color: "white",
                fontFamily: "basic-sans",
                height: "100%", // Stretch height to fill parent
                width: "100%",
                display: "flex", // Use flexbox within the card
                flexDirection: "column",
                borderRadius: "20px",
              }}
            >
              {challenge.hero && (
                <CardMedia
                  component="img"
                  height="140"
                  image={challenge.hero}
                  alt={challenge.title}
                />
              )}
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontFamily: "basic-sans" }}
                >
                  {challenge.title}
                </Typography>
              </CardContent>
              <CardActions>
                <CustomButton
                  theme={theme}
                  onClick={() => handleSelectChallenge(challenge)}
                  sx={{
                    borderRadius: "50px",
                    backgroundColor: theme.colors.darkBlue,
                    "&:hover": {
                      backgroundColor: theme.colors.amber,
                    },
                  }}
                >
                  Select
                </CustomButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Modal to display submissions */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            fontFamily: "basic-sans",
            color: "white",
            backgroundColor: theme.colors.darkBlue,
          }}
        >
          Submissions for: {selectedChallenge?.title}
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: theme.colors.darkBlue }}>
          {submissions.length === 0 ? (
            <Typography sx={{ fontFamily: "basic-sans" }}>
              No submissions found.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {submissions.map((submission) => {
                const thumbnailUrl = submission.submissionSaleId
                  ? `https://swayer.blob.core.windows.net/${submission.submissionSaleId}-pub/_thumb_large.jpg`
                  : null;

                return (
                  <Grid
                    item
                    key={submission.id}
                    xs={12}
                    sm={6}
                    md={4}
                    sx={{ display: "flex" }}
                  >
                    <Card
                      onClick={() => toggleSelect(submission.id)}
                      sx={{
                        fontFamily: "basic-sans",
                        backgroundColor: theme.colors.mediumPurple,
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "20px",
                        border: selectedSubmissions.includes(submission.id)
                          ? `2px solid ${theme.colors.green}` // Highlight selected
                          : `2px solid ${theme.colors.darkBlue}`, // Default border
                      }}
                    >
                      {thumbnailUrl && (
                        <CardMedia
                          component="div"
                          sx={{
                            paddingTop: "56.25%", // 16:9 aspect ratio
                            backgroundImage: `url(${thumbnailUrl})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                          alt={submission.title || "Submission Thumbnail"}
                        />
                      )}
                      <CardContent>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "basic-sans",
                            color: "white",
                            fontWeight: 700,
                          }}
                        >
                          {submission.title || submission.id}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: theme.colors.darkBlue }}>
          <CustomButton
            onClick={handleSelectAll}
            theme={theme}
            sx={{
              backgroundColor: theme.colors.mediumPurple,
              "&:hover": {
                backgroundColor: theme.colors.amber,
              },
            }}
          >
            Select All
          </CustomButton>

          <CustomButton
            onClick={handleSave}
            disabled={selectedSubmissions.length === 0}
            theme={theme}
            sx={{
              backgroundColor:
                selectedSubmissions.length === 0
                  ? `${theme.colors.palePurple} !important` // Custom color for disabled state
                  : theme.colors.mediumPurple, // Default background color
                  
                  color:
                  selectedSubmissions.length === 0
                  ? `${theme.colors.darkPurple} !important` // Custom color for disabled state
                  : "white", // Default background color
              "&:hover": {
                backgroundColor:
                  selectedSubmissions.length === 0
                    ? theme.colors.gray // Keep the same color when disabled
                    : theme.colors.green, // Hover color when enabled
              },
              opacity: selectedSubmissions.length === 0 ? 1 : 1, // Keep opacity consistent with color
              cursor:
                selectedSubmissions.length === 0 ? "not-allowed" : "pointer", // Proper cursor
            }}
          >
            Save
          </CustomButton>

          <CustomButton
            onClick={handleClose}
            theme={theme}
            sx={{
              backgroundColor: theme.colors.mediumPurple,
              "&:hover": {
                backgroundColor: theme.colors.red,
              },
            }}
          >
            Cancel
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChallengeList;
